import { Row, Col, Image, Button, Tooltip, OverlayTrigger, Form } from 'react-bootstrap'
import { Plus } from 'lucide-react'
import { formatTimeZone, formatCurrencyAutoLocale, isZeroDecimalCurrency } from '@/utils/tools'
import { logos } from '@/utils/publisher-logos-theme'
import AccountChoiceModal from '@/components/campaigns/AccountSourceChoiceModal'
import AdAccountStatus from '@/components/campaigns/AdAccountStatus'
import TrashIcon from '@/components/images/TrashIcon'
import ReauthenticateIcon from '@/components/images/ReauthenticateIcon'
import RefreshIcon from '@/components/images/RefreshIcon'
import { Info } from 'lucide-react'
import AddAccountsForm from './form'
import { CubeLoader } from '@/components/ui/cube-loader'

const Publisher = ({
    publisher,
    connectedPublisher,
    theme,
    isSystem,
    handleRefresh,
    handleConfirm,
    setSelectedPublisherToDelete,
    setShowDeleteModal,
    openConnectionDialog,
    setOpenConnectionDialog,
    setPixelId,
    setSelectedPublisherData,
    resetValidation,
    onChange,
    formValues,
    savingPublisher,
    accMissmatch,
    userAdAccount,
    requestTOSClick,
    validated,
    setFormValuesChanged,
    setUserOrSystemAccount,
    submitSettings,
    syncing,
    allAdAccounts,
}) => {
    const currency = connectedPublisher?.currency

    // Aggregate all Instagram accounts from all ad accounts for this publisher
    const getAllInstagramAccounts = () => {
        const publisherAdAccounts = allAdAccounts || []
        const allPages = []

        publisherAdAccounts.forEach((adAccount) => {
            if (adAccount.instagram_accounts) {
                allPages.push(...adAccount.instagram_accounts)
            }
        })

        return allPages
    }

    return (
        <div key={publisher.id} className="" style={{ background: 'transparent' }}>
            <div className={`d-flex justify-content-between align-items-center`}>
                {connectedPublisher && (
                    <div className="d-flex align-items-center gap-2">
                        <Image
                            src={logos[publisher.name][theme]}
                            alt={publisher.name}
                            className="img-fluid avatar-30 object-contain"
                        />
                        <strong className={`${theme === 'dark' ? 'text-white' : 'text-dark'} middle-text-${theme} `}>
                            {publisher.name} Ad Account
                        </strong>
                    </div>
                )}
                <div className="tw:flex tw:gap-6">
                    {!isSystem[publisher.name] && connectedPublisher && (
                        <Button
                            size="sm"
                            disabled={syncing}
                            onClick={() => handleRefresh(publisher.name)}
                            variant={`outline-${theme === 'dark' ? 'light' : 'dark'}`}
                            style={{ display: 'inline-flex' }}
                            className="tw:h-[36px] py-2 tw:!ml-0 media-buying-button-action justify-content-center align-items-center gap-1"
                        >
                            <RefreshIcon /> Refresh
                        </Button>
                    )}
                    {!isSystem[publisher.name] && connectedPublisher && (
                        <Button
                            size="sm"
                            style={{ display: 'inline-flex' }}
                            onClick={() => handleConfirm(publisher.name)}
                            variant={`outline-${theme === 'dark' ? 'light' : 'dark'}`}
                            className="tw:h-[36px] tw:!ml-0 py-2 media-buying-button-action justify-content-center align-items-center gap-1"
                        >
                            <ReauthenticateIcon /> Reauthenticate
                        </Button>
                    )}
                    {connectedPublisher && (
                        <Button
                            size="sm"
                            variant={`outline-${theme === 'dark' ? 'light' : 'dark'}`}
                            style={{ display: 'inline-flex' }}
                            className={`tw:h-[36px] py-2 tw:!ml-0 text-white media-buying-button-danger delete-${theme} justify-content-center align-items-center gap-1`}
                            onClick={() => {
                                setSelectedPublisherToDelete(connectedPublisher)
                                setShowDeleteModal(true)
                            }}
                        >
                            <TrashIcon /> Delete Account
                        </Button>
                    )}
                </div>
            </div>

            <div
                style={{
                    background: 'transparent',
                    borderTopStyle: 'none!important',
                }}
            >
                {!connectedPublisher ? (
                    savingPublisher === publisher.name ? (
                        <div
                            className="d-flex justify-content-center align-items-center flex-column text-center py-5"
                            style={{ minHeight: '300px' }}
                        >
                            <CubeLoader size="default" />
                        </div>
                    ) : (
                        <div
                            className="d-flex justify-content-center align-items-center flex-column text-center py-5"
                            style={{ minHeight: '300px' }}
                        >
                            <div
                                className="d-flex justify-content-center align-items-center rounded-circle mb-3"
                                style={{
                                    width: '80px',
                                    height: '80px',
                                }}
                            >
                                <Image
                                    src={
                                        publisher.name === 'Meta'
                                            ? theme === 'light'
                                                ? logos.Meta.light
                                                : logos.Meta.dark
                                            : logos[publisher.name]?.[theme]
                                    }
                                    alt={`${publisher.name} logo`}
                                    style={{ height: '80px' }}
                                />
                            </div>
                            <p className={`text-muted-havent-connect-meta-${theme} mb-3`}>
                                You haven't connected {publisher.name} <br />
                                ad account yet
                            </p>
                            <Button
                                variant="primary"
                                size="sm"
                                className="px-4 py-2 d-flex align-items-center no-border-btn d-flex gap-1"
                                onClick={() => setOpenConnectionDialog(true)}
                            >
                                <Plus size={14} />
                                <span className="fw-medium">Add {publisher.name} Account</span>
                            </Button>
                            <AccountChoiceModal
                                show={openConnectionDialog}
                                publisher={publisher.name}
                                onCancel={() => setOpenConnectionDialog(false)}
                                onConfirm={handleConfirm}
                            />
                        </div>
                    )
                ) : (
                    <>
                        <Row className="mb-4 table-ad-account">
                            <Col md={2}>
                                <div className={`text-white column-name-${theme}`}>Status</div>
                                <AdAccountStatus
                                    adAccount={userAdAccount}
                                    statusMismatch={accMissmatch[publisher.name]}
                                    syncing={syncing}
                                    tosAccepted={connectedPublisher.tos_accepted}
                                    tosClickHandler={requestTOSClick.bind(this, connectedPublisher)}
                                    isSystem={isSystem[publisher.name]}
                                />
                                {/* If you need another Accept toS */}

                                {/* {!connectedPublisher.tos_accepted && (
                                                            <div className="status-warning-message d-flex align-items-center mt-1">
                                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20Z" stroke="#CE9A00" stroke-width="1.5"/>
                                                                    <path d="M12 13L12 8" stroke="#CE9A00" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                                                    <path d="M12 15.75L12 16" stroke="#CE9A00" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                                                </svg>

                                                                Accept ToS
                                                            </div>
                                                        )} */}
                            </Col>
                            <Col md={3}>
                                <div className={`text-white column-name-${theme}`}>Account Name</div>
                                <div className={`text-white fw-color-leggera column-name-${theme}`}>
                                    {connectedPublisher.ad_account?.replace('act_', '') || ''} -{' '}
                                    {connectedPublisher.ad_account_name}
                                </div>
                            </Col>
                            <Col md={2}>
                                <div className={`text-white column-name-${theme}`}>Account Type</div>
                                <div className={`text-white fw-color-leggera column-name-${theme}`}>
                                    {setUserOrSystemAccount(connectedPublisher)}
                                </div>
                            </Col>
                            <Col md={3}>
                                <div
                                    // className={`tw:flex tw:items-center tw:gap-1 tw:text-white tw:column-name-${theme}`}
                                    className={`text-white column-name-${theme}`}
                                >
                                    Name Stamp{' '}
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip className={`media-buying-tool-tip ${theme}`}>
                                                Label used in reporting
                                            </Tooltip>
                                        }
                                    >
                                        <span className="text-white">
                                            <Info
                                                className={`tw:w-[22px] tw:h-[22px] tw:inline-block tw:pr-1 column-name-${theme}`}
                                            />
                                        </span>
                                    </OverlayTrigger>
                                </div>
                                <div className="text-white fw-color-leggera">
                                    {isSystem[publisher.name] ? (
                                        <span className={`text-white column-name-${theme}`}>
                                            {connectedPublisher.label}
                                        </span>
                                    ) : (
                                        <Form.Group>
                                            <Form.Control
                                                className="media-buying-input input-overflow my-1"
                                                type="text"
                                                name="label"
                                                maxLength={100}
                                                value={formValues[publisher.name]?.label}
                                                isValid={validated && connectedPublisher.label}
                                                onChange={(ev) => {
                                                    onChange(ev, publisher.name)
                                                }}
                                                placeholder="Turba Media"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {isSystem[publisher.name] ? '' : 'Label is invalid'}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    )}
                                </div>
                            </Col>
                        </Row>

                        {/* 2nd row of info: 4 columns */}
                        <Row className="mb-4">
                            <Col md={2}>
                                <div className={`text-white column-name-${theme}`}>Last 30 Days Spent</div>
                                <div className={`fw-color-leggera column-name-${theme}`}>
                                    {isSystem[publisher.name]
                                        ? 'n/a'
                                        : !userAdAccount || userAdAccount.month_spend === null
                                          ? '' // Show nothing while loading (null state)
                                          : userAdAccount.month_spend === undefined
                                            ? '-' // Show dash for undefined/failed API
                                            : formatCurrencyAutoLocale({
                                                  amount: Number(userAdAccount.month_spend),
                                                  showCode: true,
                                                  currency,
                                              })}
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className={`text-white column-name-${theme}`}>Total Ad Spent</div>
                                <div className={`fw-color-leggera column-name-${theme}`}>
                                    {isSystem[publisher.name]
                                        ? 'n/a'
                                        : !userAdAccount || userAdAccount.amount_spent === null
                                          ? '' // Show nothing while loading (null state)
                                          : userAdAccount.amount_spent === undefined
                                            ? '-' // Show dash for undefined/failed API
                                            : formatCurrencyAutoLocale({
                                                  amount: isZeroDecimalCurrency(currency)
                                                      ? Number(userAdAccount.amount_spent)
                                                      : Number(userAdAccount.amount_spent) / 100,
                                                  showCode: true,
                                                  currency,
                                              })}
                                </div>
                            </Col>
                            <Col md={2}>
                                <div className={`text-white column-name-${theme}`}>Time Zone</div>
                                <div className={`fw-color-leggera column-name-${theme}`}>
                                    {connectedPublisher.timezone}
                                </div>
                                <div className={`fw-color-leggera column-name-${theme}`}>
                                    {formatTimeZone(connectedPublisher.timezone_offset)} UTC
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className={`text-white column-name-${theme}`}>Top 3 Metrics</div>
                                <div className={`fw-color-leggera column-name-${theme}`}>
                                    ROAS (All), Cost per Add to Cart, Cost per Purchase
                                </div>
                            </Col>
                        </Row>

                        <AddAccountsForm
                            publisher={publisher}
                            setPixelId={setPixelId}
                            userAdAccount={userAdAccount}
                            resetValidation={resetValidation}
                            setFormValuesChanged={setFormValuesChanged}
                            setSelectedPublisherData={setSelectedPublisherData}
                            selectedPixel={formValues[publisher.name]?.pixel}
                            selectedPageId={formValues[publisher.name]?.page_id}
                            selectedInstagramId={formValues[publisher.name]?.instagram_id}
                            disabled={connectedPublisher?.type?.toLowerCase() === 'system'}
                            submitSettings={submitSettings}
                            formValues={formValues}
                            allInstagramAccounts={getAllInstagramAccounts()}
                        />
                    </>
                )}
                <hr
                    style={{
                        border: 'none',
                        height: '1px',
                        background: '#ccc',
                        width: 'calc(100% + 3rem)',
                        margin: '3rem -1.5rem',
                    }}
                />
            </div>
        </div>
    )
}

export default Publisher
